import { AssignmentInd, Clear, Delete, Edit, Restore } from '@mui/icons-material';
import { Alert, AlertTitle, Grid, Typography, List, ListItemText, Button, IconButton, ListItem, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import useDistrictPickerHeader from 'src/shared/hooks/useDistrictPickerHeader';
import { User } from 'src/shared/models/user';
import { useFormContext } from 'react-hook-form';
import { useMemo } from 'react';
import { useGlobals } from 'src/shared/contexts/appContext';
import { UserEditDistrictDropdown } from './userEditDistrictDropdown';
import { UserEditAssociationDropdown } from './userEditAssociationDropdown';

type UserEditMembershipSummaryProps = {
  user: User;
  setShowDistrictDropdown: (districtDropdown: boolean) => void;
  setShowAssociationDropdown: (associationDropdown: boolean) => void;
  showAssociationDropdown: boolean;
  showDistrictDropdown: boolean;
};
export const UserEditMembershipSummary = (props: UserEditMembershipSummaryProps) => {
  const { user, setShowDistrictDropdown, setShowAssociationDropdown, showAssociationDropdown, showDistrictDropdown } = props;
  const { t } = useTranslation(['user']);
  const { currentUser } = useGlobals();
  const { setValue, watch } = useFormContext();

  const districts = watch('districts');
  const associations = watch('associations');

  const { districtName, associationName } = useDistrictPickerHeader();
  const hasCustomer = () => user.customer && user.customer.countryAss;

  const resetButton = () => {
    setValue('districts', user.districts);
    setValue('associations', user.associations);
  };

  const hasChanges = useMemo(() => {
    const districtsChanged = JSON.stringify(districts) !== JSON.stringify(user.districts);
    const associationsChanged = JSON.stringify(associations) !== JSON.stringify(user.associations);
    return districtsChanged || associationsChanged;
  }, [districts, associations, user.districts, user.associations]);

  return (
    <Alert
      severity='info'
      icon={<AssignmentInd />}
      sx={{ border: (thm) => `1px solid ${thm.palette.divider}`, '& .MuiAlert-message': { width: '100% !important' } }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', marginBottom: 1 }}>
        <AlertTitle sx={{ fontWeight: 600 }}>{t('user:editUser.membershipHeader')}</AlertTitle>
      </Box>
      <Grid container sx={{ width: '100%' }}>
        <Grid item xs={12}>
          {hasCustomer() && (
            <>
              <Typography sx={{ fontWeight: 400 }} gutterBottom>
                {t('user:editUser.organisation')}
              </Typography>
              <List dense sx={{ m: 0, p: 0 }}>
                <ListItemText primary={user.customer.countryAss} />
              </List>
            </>
          )}

          {!hasCustomer() && (
            <>
              <Grid container>
                {/* Distrikts */}
                {user.districts && !!currentUser && currentUser?.districtCount > 0 && (
                  <Grid item xs={6}>
                    <Typography gutterBottom>
                      <Box component='span' sx={{ fontWeight: 500 }}>
                        {districtName}
                      </Box>{' '}
                      <Box component='small' sx={{ fontWeight: 400 }}>
                        ({Array.isArray(districts) ? districts.length : 0})
                      </Box>
                      {!showDistrictDropdown && (
                        <IconButton
                          size='small'
                          onClick={() => {
                            setShowAssociationDropdown(false);
                            setShowDistrictDropdown(!showDistrictDropdown);
                          }}
                          sx={{ marginTop: -1, marginLeft: 0.5 }}
                        >
                          <Edit fontSize='small' />
                        </IconButton>
                      )}
                    </Typography>
                    {districts != null && districts?.length > 0 && (
                      <Grid container>
                        <List dense sx={{ m: 0, p: 0 }}>
                          {districts.map((item) => (
                            <ListItem key={item.countyAss_id}>
                              <ListItemText primary={item.countyAss} sx={{ marginRight: 1, marginLeft: -1.5 }} />
                              <IconButton
                                size='small'
                                onClick={() => setValue('districts', [])}
                                sx={{
                                  padding: 0,
                                  marginTop: -1,
                                }}
                              >
                                <Delete fontSize='inherit' />
                              </IconButton>
                            </ListItem>
                          ))}
                        </List>
                      </Grid>
                    )}

                    <Grid container mt={2.5}>
                      <Grid item xs={10} mr={2}>
                        {showDistrictDropdown && <UserEditDistrictDropdown setShowDistrictDropdown={setShowDistrictDropdown} />}
                      </Grid>
                      <Grid item xs={1} alignContent='center' mt={1}>
                        {showDistrictDropdown && (
                          <IconButton
                            size='small'
                            onClick={() => {
                              setShowAssociationDropdown(false);
                              setShowDistrictDropdown(!showDistrictDropdown);
                            }}
                            sx={{
                              padding: 0,
                              marginTop: -1,
                            }}
                          >
                            <Clear fontSize='inherit' />
                          </IconButton>
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                )}
                {/* Föreningar */}
                {user.associations && (
                  <Grid item xs={6}>
                    <Typography gutterBottom>
                      <Box component='span' sx={{ fontWeight: 500 }}>
                        {associationName}
                      </Box>{' '}
                      <Box component='small' sx={{ fontWeight: 400 }}>
                        ({Array.isArray(associations) ? associations.length : 0})
                      </Box>
                      {!showAssociationDropdown && (
                        <IconButton
                          size='small'
                          onClick={() => {
                            setShowDistrictDropdown(false);
                            setShowAssociationDropdown(!showAssociationDropdown);
                          }}
                          sx={{ marginTop: -1, marginLeft: 0.5 }}
                        >
                          <Edit fontSize='small' />
                        </IconButton>
                      )}
                    </Typography>
                    {associations != null && associations?.length > 0 && (
                      <Grid container>
                        <List dense sx={{ m: 0, p: 0 }}>
                          {associations.map((item) => (
                            <ListItem key={item.localAss_id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <ListItemText primary={item.localAss} sx={{ marginRight: 1, marginLeft: -1.5 }} />
                              <IconButton
                                size='small'
                                onClick={() => setValue('associations', [])}
                                sx={{
                                  padding: 0,
                                  marginTop: -1,
                                }}
                              >
                                <Delete fontSize='inherit' />
                              </IconButton>
                            </ListItem>
                          ))}
                        </List>
                      </Grid>
                    )}

                    <Grid container mt={2.5}>
                      <Grid item xs={10} mr={2}>
                        {showAssociationDropdown && <UserEditAssociationDropdown setShowAssociationDropdown={setShowAssociationDropdown} />}
                      </Grid>
                      <Grid item xs={1} alignContent='center' mt={1}>
                        {showAssociationDropdown && (
                          <IconButton
                            size='small'
                            onClick={() => {
                              setShowDistrictDropdown(false);
                              setShowAssociationDropdown(!showAssociationDropdown);
                            }}
                            sx={{
                              padding: 0,
                              marginTop: -1,
                            }}
                          >
                            <Clear fontSize='inherit' />
                          </IconButton>
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                )}
              </Grid>
              {hasChanges && (
                <Box display='flex' justifyContent='flex-end' mt={2}>
                  <Button
                    variant='outlined'
                    size='small'
                    onClick={() => {
                      resetButton();
                      setShowDistrictDropdown(false);
                      setShowAssociationDropdown(false);
                    }}
                    endIcon={<Restore />}
                  >
                    {t('user:restore')}
                  </Button>
                </Box>
              )}
            </>
          )}
        </Grid>
      </Grid>
    </Alert>
  );
};
